/**
 * `GET /portal/domains/:id/dns-records` のレスポンスを画面が扱う形へ変換する。
 *
 * API は 1 レコードにつき「期待値・観測値・判定」を **1 つのオブジェクトに同居**
 * させて返す（`items[]`）。一方 UI は「案内用の一覧（records）」と
 * 「検証結果の一覧（checks）」を別々に受け取る前提で書かれていた。
 *
 * どちらを正にするか: **API の形を正とする**。
 * records と checks を別配列にすると両者を添字で突き合わせることになり、
 * 片方だけ並び順が変わった瞬間に「MX の判定が SPF の行に出る」という
 * 気付きにくい壊れ方をする。同居していればその事故は起こり得ない。
 *
 * UI 側のコンポーネントを書き換える代わりにこのアダプタを 1 か所に置いて、
 * 変換の責任を明示している。
 */
import type { DnsCheckResult, ExpectedDnsRecord } from '@mail-sv/shared';

/** API が実際に返す 1 レコード。 */
export interface DnsRecordItem {
  id: string;
  kind: ExpectedDnsRecord['kind'];
  name: string;
  type: ExpectedDnsRecord['type'];
  priority: number | null;
  expectedValue: string;
  ttl: number;
  required: boolean;
  observedValue: string | null;
  result: DnsCheckResult;
  checkedAt: string | null;
  description: string;
}

/** API が実際に返すレスポンス全体。 */
export interface DnsRecordsApiResponse {
  items: DnsRecordItem[];
  total: number;
  verificationToken: string;
  mailHostname: string;
}

export type DnsOverall = 'verified' | 'partial' | 'failed' | 'unchecked';

export interface DnsRecordsView {
  domain: string;
  records: ExpectedDnsRecord[];
  checks: Array<{
    kind: string;
    name: string;
    result: DnsCheckResult;
    observed: string | null;
    checkedAt: string | null;
  }>;
  overall: DnsOverall;
  checkedAt: string | null;
  mailHostname: string;
  verificationToken: string;
}

/**
 * 全体判定。**必須レコードだけ**で決める。
 * autoconfig / autodiscover のような任意レコードが未設定でも
 * 「DNS が壊れている」と見せてしまうと、契約者が直しようのない警告を
 * 延々と見ることになるため。
 */
export function deriveOverall(items: DnsRecordItem[]): DnsOverall {
  const required = items.filter((i) => i.required);
  if (required.length === 0) return 'unchecked';
  if (required.every((i) => i.result === 'unchecked')) return 'unchecked';
  if (required.every((i) => i.result === 'ok')) return 'verified';
  if (required.some((i) => i.result === 'ok')) return 'partial';
  return 'failed';
}

export function toDnsRecordsView(res: DnsRecordsApiResponse, domain: string): DnsRecordsView {
  const checkedAts = res.items.map((i) => i.checkedAt).filter((v): v is string => v !== null);

  return {
    domain,
    records: res.items.map((i) => ({
      kind: i.kind,
      name: i.name,
      type: i.type,
      priority: i.priority,
      value: i.expectedValue,
      ttl: i.ttl,
      required: i.required,
      description: i.description,
    })),
    checks: res.items.map((i) => ({
      kind: i.kind,
      name: i.name,
      result: i.result,
      observed: i.observedValue,
      checkedAt: i.checkedAt,
    })),
    overall: deriveOverall(res.items),
    // 「いつ時点の判定か」は最も新しい検証時刻で代表させる。
    checkedAt: checkedAts.length > 0 ? checkedAts.sort().at(-1)! : null,
    mailHostname: res.mailHostname,
    verificationToken: res.verificationToken,
  };
}
